import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Tabs, Input, Pagination, Row, Col, Card, Tag, Badge, Tooltip } from 'antd';
import { 
  TagOutlined, 
  PercentageOutlined, 
  FireOutlined,
  ThunderboltOutlined,
  StarOutlined,
  CrownOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  HeartOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { TabPane } = Tabs;

function Discount() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [currentProductId, setCurrentProductId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(48);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    // Load favorites from localStorage
    const savedFavorites = localStorage.getItem('discountFavorites');
    if (savedFavorites) {
      setFavorites(new Set(JSON.parse(savedFavorites)));
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const q = query(collection(db, 'products'));
      const querySnapshot = await getDocs(q);
      const categoriesSet = new Set();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const category = data?.category;
        if (category) categoriesSet.add(category);
      });
      setCategories([...categoriesSet]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async (category = null, search = '', page = 1) => {
    setLoading(true);
    try {
      let q;
      if (category) {
        q = query(collection(db, 'products'), where('category', '==', category));
      } else {
        q = query(collection(db, 'products'));
      }

      const querySnapshot = await getDocs(q);
      const totalProductsCount = querySnapshot.size;
      setTotalProducts(totalProductsCount);

      // Safely filter products with discount >= 1
      const productsData = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((product) => {
          const title = product?.title || '';
          const productCategory = product?.category || '';
          const discount = product?.discount || 0;
          const searchTerm = search || '';
          
          return (
            discount >= 1 &&
            (title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            productCategory.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        });

      // Sort by discount percentage (highest first)
      productsData.sort((a, b) => (b.discount || 0) - (a.discount || 0));

      const paginatedProducts = productsData.slice(
        (page - 1) * productsPerPage,
        page * productsPerPage
      );

      setProducts(paginatedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    fetchProducts(null, value, 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProducts(null, searchQuery, page);
  };

  const calculateDiscountedPrice = (price, discount) => {
    return price - (price * discount) / 100;
  };

  const calculateSavings = (price, discount) => {
    return (price * discount) / 100;
  };

  const getDiscountBadge = (discount) => {
    if (discount >= 50) {
      return {
        text: "MEGA SALE",
        color: "#f50",
        icon: <FireOutlined />,
        bgColor: "#fff2e8"
      };
    } else if (discount >= 30) {
      return {
        text: "HOT DEAL",
        color: "#eb2f96",
        icon: <ThunderboltOutlined />,
        bgColor: "#fff0f6"
      };
    } else if (discount >= 20) {
      return {
        text: "SPECIAL OFFER",
        color: "#722ed1",
        icon: <CrownOutlined />,
        bgColor: "#f9f0ff"
      };
    } else {
      return {
        text: "DISCOUNT",
        color: "#52c41a",
        icon: <PercentageOutlined />,
        bgColor: "#f6ffed"
      };
    }
  };

  const handleImageClick = (productId, imageUrl) => {
    setCurrentImage(imageUrl);
    setCurrentProductId(productId);
    setIsModalVisible(true);
  };

  const handleProductClick = (productId) => {
    navigate(`/product-details/${productId}`);
  };

  const handleOk = () => {
    navigate(`/product-details/${currentProductId}`);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const toggleFavorite = (productId, e) => {
    e.stopPropagation();
    const newFavorites = new Set(favorites);
    if (newFavorites.has(productId)) {
      newFavorites.delete(productId);
    } else {
      newFavorites.add(productId);
    }
    setFavorites(newFavorites);
    localStorage.setItem('discountFavorites', JSON.stringify([...newFavorites]));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    }}>
      {/* Header Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        padding: '30px 20px',
        background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
        borderRadius: '20px',
        boxShadow: '0 10px 30px rgba(255, 107, 53, 0.2)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
        }} />
        
        <h1 style={{
          fontSize: '36px',
          fontWeight: 800,
          marginBottom: '10px',
          textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
        }}>
          🎯 FLASH DEALS & DISCOUNTS
        </h1>
        
        <p style={{
          fontSize: '18px',
          marginBottom: '20px',
          opacity: 0.9,
          maxWidth: '600px',
          margin: '0 auto 20px',
        }}>
          Limited time offers! Don't miss out on these incredible savings
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}>
          <Tag color="white" style={{ color: '#ff6b35', fontWeight: 600, padding: '8px 16px' }}>
            <FireOutlined /> Hot Deals
          </Tag>
          <Tag color="white" style={{ color: '#ff6b35', fontWeight: 600, padding: '8px 16px' }}>
            <ThunderboltOutlined /> Flash Sales
          </Tag>
          <Tag color="white" style={{ color: '#ff6b35', fontWeight: 600, padding: '8px 16px' }}>
            <PercentageOutlined /> Up to 70% OFF
          </Tag>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
        marginBottom: '30px',
      }}>
        <Input
          placeholder="🔍 Search for amazing deals..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="large"
          style={{
            marginBottom: '20px',
            width: '100%',
            borderRadius: '12px',
            padding: '15px 20px',
            fontSize: '16px',
            borderColor: '#ffa500',
            boxShadow: '0 2px 10px rgba(255, 165, 0, 0.1)',
          }}
        />

        <div style={{ marginBottom: '20px' }}>
          <Tabs
            defaultActiveKey="all"
            onChange={(key) => {
              setCurrentPage(1);
              fetchProducts(key === 'all' ? null : key, searchQuery, 1);
            }}
            centered
            tabBarStyle={{ marginBottom: 0 }}
          >
            <TabPane 
              tab={
                <span style={{ fontSize: '16px', fontWeight: 600 }}>
                  <StarOutlined style={{ marginRight: '8px' }} />
                  All Deals
                </span>
              } 
              key="all" 
            />
            {categories.map((category) => (
              <TabPane 
                tab={
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    {category}
                  </span>
                } 
                key={category} 
              />
            ))}
          </Tabs>
        </div>
      </div>

      {/* Products Grid */}
      <Row gutter={[20, 20]} justify="center">
        {products.map((product) => {
          const newPrice = calculateDiscountedPrice(product.price, product.discount);
          const savings = calculateSavings(product.price, product.discount);
          const discountBadge = getDiscountBadge(product.discount);
          const isFavorite = favorites.has(product.id);
          
          return (
            <Col xs={12} sm={8} md={6} lg={6} key={product.id}>
              <Badge.Ribbon 
                text={
                  <span style={{ 
                    fontSize: '10px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    {discountBadge.icon}
                    {discountBadge.text}
                  </span>
                }
                color={discountBadge.color}
                style={{ fontSize: '10px' }}
              >
                <Card
                  hoverable
                  style={{
                    width: '100%',
                    borderRadius: '15px',
                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                  bodyStyle={{ padding: '15px' }}
                  onClick={() => handleProductClick(product.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.boxShadow = '0 15px 30px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  {/* Favorite Button */}
                  <Button
                    type="text"
                    shape="circle"
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      zIndex: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                    onClick={(e) => toggleFavorite(product.id, e)}
                  >
                    <HeartOutlined style={{ 
                      color: isFavorite ? '#ff4d4f' : '#999',
                      fontSize: '18px',
                      transition: 'color 0.3s',
                    }} />
                  </Button>

                  {/* Discount Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    zIndex: 2,
                    backgroundColor: discountBadge.bgColor,
                    color: discountBadge.color,
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}>
                    {discountBadge.icon}
                    <span>{product.discount}% OFF</span>
                  </div>

                  {/* Product Image */}
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '180px',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    marginBottom: '15px',
                    cursor: 'pointer',
                  }}>
                    <img
                      src={product.image}
                      alt={product.title || 'Product Image'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(product.id, product.image);
                      }}
                    />
                    
                    {/* Quick View Overlay */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                      padding: '10px',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = 0;
                    }}
                    >
                      <Button
                        type="primary"
                        ghost
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageClick(product.id, product.image);
                        }}
                        style={{
                          width: '100%',
                          borderColor: 'white',
                          color: 'white',
                        }}
                      >
                        Quick View
                      </Button>
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '10px',
                      color: '#333',
                      height: '40px',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      <TagOutlined style={{ 
                        color: '#ff6b35', 
                        marginRight: '8px', 
                        fontSize: '14px' 
                      }} />
                      {product.title || 'No Title'}
                    </h3>
                    
                    {/* Price Section */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      marginBottom: '15px',
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}>
                        <span style={{
                          fontWeight: 'bold',
                          color: '#ff5722',
                          fontSize: '18px',
                          background: 'linear-gradient(135deg, #ff6b35, #ffa500)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}>
                          {formatPrice(newPrice)}
                        </span>
                        
                        <span style={{
                          textDecoration: 'line-through',
                          color: '#999',
                          fontSize: '13px',
                        }}>
                          {formatPrice(product.price || 0)}
                        </span>
                      </div>
                      
                      {/* Savings Badge */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#f6ffed',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        width: 'fit-content',
                      }}>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: '#52c41a',
                        }}>
                          💰 Save {formatPrice(savings)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '15px',
                    }}>
                      <Button
                        type="primary"
                        size="small"
                        block
                        icon={<ShoppingCartOutlined />}
                        style={{
                          background: 'linear-gradient(135deg, #ff6b35, #ffa500)',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.id);
                        }}
                      >
                        Buy Now
                      </Button>
                      
                      <Tooltip title="Quick View">
                        <Button
                          type="default"
                          size="small"
                          icon={<EyeOutlined />}
                          style={{
                            borderRadius: '8px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(product.id, product.image);
                          }}
                        />
                      </Tooltip>
                    </div>
                    
                    {/* Limited Time Badge */}
                    {product.discount >= 30 && (
                      <div style={{
                        marginTop: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        backgroundColor: '#fff7e6',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px dashed #ffa940',
                      }}>
                        <ClockCircleOutlined style={{ color: '#fa8c16' }} />
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#fa8c16',
                        }}>
                          Limited Time Offer
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              </Badge.Ribbon>
            </Col>
          );
        })}
      </Row>

      {/* Empty State */}
      {products.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'white',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
          marginTop: '20px',
        }}>
          <div style={{
            fontSize: '80px',
            marginBottom: '20px',
            opacity: 0.2,
          }}>
            🔍
          </div>
          <h3 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#666',
            marginBottom: '10px',
          }}>
            No Deals Found
          </h3>
          <p style={{
            color: '#999',
            maxWidth: '400px',
            margin: '0 auto',
          }}>
            Try searching for something else or check back later for new offers!
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'white',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
          marginTop: '20px',
        }}>
          <div className="ant-spin ant-spin-lg" style={{ display: 'inline-block' }}>
            <span className="ant-spin-dot ant-spin-dot-spin">
              <i className="ant-spin-dot-item" style={{ backgroundColor: '#ff6b35' }}></i>
              <i className="ant-spin-dot-item" style={{ backgroundColor: '#ff6b35' }}></i>
              <i className="ant-spin-dot-item" style={{ backgroundColor: '#ff6b35' }}></i>
              <i className="ant-spin-dot-item" style={{ backgroundColor: '#ff6b35' }}></i>
            </span>
          </div>
          <p style={{
            marginTop: '20px',
            color: '#666',
            fontWeight: '500',
          }}>
            Loading amazing deals...
          </p>
        </div>
      )}

      {/* Pagination */}
      {products.length > 0 && (
        <div style={{ 
          marginTop: '40px',
          display: 'flex', 
          justifyContent: 'center',
          background: 'white',
          padding: '20px',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
        }}>
          <Pagination
            current={currentPage}
            total={totalProducts}
            pageSize={productsPerPage}
            onChange={handlePageChange}
            showSizeChanger={false}
            itemRender={(current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <Button
                    type={currentPage === current ? 'primary' : 'default'}
                    style={{
                      margin: '0 4px',
                      borderRadius: '8px',
                      borderColor: currentPage === current ? '#ff6b35' : '#d9d9d9',
                      backgroundColor: currentPage === current ? '#ff6b35' : 'white',
                    }}
                  >
                    {current}
                  </Button>
                );
              }
              return originalElement;
            }}
            showTotal={(total, range) => (
              <span style={{ 
                marginRight: '20px', 
                color: '#666',
                fontWeight: '500',
              }}>
                Showing {range[0]}-{range[1]} of {total} amazing deals
              </span>
            )}
          />
        </div>
      )}

      {/* Image Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <EyeOutlined style={{ color: '#ff6b35' }} />
            <span>Product Preview</span>
          </div>
        }
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button 
            key="back" 
            onClick={handleCancel}
            style={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleOk}
            style={{
              background: 'linear-gradient(135deg, #ff6b35, #ffa500)',
              border: 'none',
              borderRadius: '8px',
            }}
          >
            <ShoppingCartOutlined /> View Details & Buy
          </Button>,
        ]}
        width={800}
        bodyStyle={{ padding: '20px' }}
      >
        <div style={{
          width: '100%',
          height: '400px',
          overflow: 'hidden',
          borderRadius: '12px',
          marginBottom: '20px',
        }}>
          <img 
            src={currentImage} 
            alt="Product" 
            style={{ 
              width: '100%', 
              height: '100%',
              objectFit: 'contain',
            }} 
          />
        </div>
        <p style={{ textAlign: 'center', color: '#666' }}>
          Click "View Details & Buy" to see full product information
        </p>
      </Modal>

      {/* Info Banner */}
      <div style={{
        marginTop: '40px',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        color: 'white',
        padding: '25px',
        borderRadius: '15px',
        textAlign: 'center',
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
        }}>
          <ThunderboltOutlined style={{ color: '#ffa500' }} />
          Why Shop Our Discounts?
        </h3>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '30px',
          flexWrap: 'wrap',
          marginTop: '20px',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '200px' }}>
            <div style={{
              fontSize: '32px',
              color: '#ffa500',
              marginBottom: '10px',
            }}>
              💰
            </div>
            <div style={{ fontWeight: '600' }}>Best Prices</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Guaranteed lowest prices</div>
          </div>
          <div style={{ textAlign: 'center', maxWidth: '200px' }}>
            <div style={{
              fontSize: '32px',
              color: '#ffa500',
              marginBottom: '10px',
            }}>
              ⚡
            </div>
            <div style={{ fontWeight: '600' }}>Fast Delivery</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Quick shipping across Kenya</div>
          </div>
          <div style={{ textAlign: 'center', maxWidth: '200px' }}>
            <div style={{
              fontSize: '32px',
              color: '#ffa500',
              marginBottom: '10px',
            }}>
              🛡️
            </div>
            <div style={{ fontWeight: '600' }}>Quality Guarantee</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>Premium quality products</div>
          </div>
          <div style={{ textAlign: 'center', maxWidth: '200px' }}>
            <div style={{
              fontSize: '32px',
              color: '#ffa500',
              marginBottom: '10px',
            }}>
              🔄
            </div>
            <div style={{ fontWeight: '600' }}>Easy Returns</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>30-day return policy</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Discount;